import PropTypes from 'prop-types';
// @flow

import React, { Component } from 'react';
import _ from 'lodash';
import moment from 'moment';

import I18n from 'PMT/i18n/I18n';

import { DateUtils, NotificationUtils } from 'PMT/utils';
import Listener from 'PMT/flux/Listener';

import Events from 'constants/Events';

import { SessionStore } from 'stores';

import { OrderAction, RouteAction } from 'actions';

// -- stores
import OrdersListStore from 'stores/OrdersListStore';
import RestaurantStore from 'stores/RestaurantStore';


// -- entities
import { Order, OrdersList } from 'PMT/entities/order';

// -- views
import AbstractComponent from 'components/AbstractComponent';
import OrderRowView from './OrderRowView';
import OrderCancelModal from 'components/order/orderCancelModal/OrderCancelModal'
import OrderDetailModal from 'components/order/orderDetailModal/OrderDetailModal';
import DateField from 'PMT/components/form/DateField';

import { ModalType } from '../constants'

let _timer = null;

export default class OrdersHistoryPage extends AbstractComponent {

  static DATE_QUERY_PARAM_FORMAT = 'DD-MM-YYYY';

  state = {
    ordersList: null,
    restaurant: RestaurantStore.getRestaurant(),

    // order to disaply details
    selectedOrder: null,

    date: this.getDefaultDate(),
    openedModalType: null,
    errorCancel: null,
    isLoadingCancelOrder: false,
  };

  componentDidMount() {
    OrdersListStore.addListeners(this.ordersListListeners);

    this.loadOrdersListForDate(this.state.date);
  }

  componentWillUnmount() {
    OrdersListStore.removeListeners(this.ordersListListeners);
  }

  get ordersListListeners(): ListenerList {
    return [
      new Listener(Events.ON_RETRIEVE_ORDERS_LIST_BY_DUE_DATE_SUCCESS, this.onOrdersListLoaded),
      new Listener(Events.ON_RETRIEVE_ORDERS_LIST_BY_DUE_DATE_FAILURE, this.onOrderListLoadError),

      new Listener(Events.UPDATE_ORDER_SUCCESS, this.onOrderUpdateSuccess),
      new Listener(Events.UPDATE_ORDER_FAILURE, this.onOrderUpdateError)
    ];
  }

  getDefaultDate() {
    let defaultDate = null;

    if (!_.isUndefined(this.queryParams.date)) {
      defaultDate = moment(
        this.queryParams.date,
        OrdersHistoryPage.DATE_QUERY_PARAM_FORMAT
      );
    }

    return !_.isNull(defaultDate) ? defaultDate : moment();
  }

  /**
   * Load the orders created during the given timelapse.
   */
  loadOrdersListForDate(date: number) {
    const fromTime = date.startOf('day').valueOf();
    const toTime = date.endOf('day').valueOf();

    this.showLoading();
    OrderAction.loadOrdersListByDueDate(fromTime, toTime);
  }

  setSelectedOrder(selectedOrder: Order, openedModalType) {
    this.setState({
      selectedOrder,
      openedModalType,
      errorCancel: null,
    });
  }

  //
  // Api events - order
  //

  onOrdersListLoaded = () => {
    const ordersList = OrdersListStore.getDueDateOrdersList();

    this.setState({
      ordersList,
      loading: false
    });
  };

  onOrderListLoadError = () => {
    // TODO.
  };

  onOrderUpdateSuccess = () => {
    this.setState({
      isLoadingCancelOrder: false,
      ordersList: OrdersListStore.getDueDateOrdersList()
    })
    this.onHideModal()
  }

  onOrderUpdateError = () => {
    this.setState({
      isLoadingCancelOrder: false,
      errorCancel: OrdersListStore.getApiError()
    })
  };

  //
  // Date events
  //

  onDateChange = (newDate: Object) => {
    this.loadOrdersListForDate(newDate);

    /*
     * Update the query params.
     */
    this.onChangeQueryUrl({
      date: newDate.format(OrdersHistoryPage.DATE_QUERY_PARAM_FORMAT)
    });

    this.setState({
      date: newDate
    });
  };

  onOrderSelected = (order: Order, openedModalType) => {
    this.setSelectedOrder(order, openedModalType);
  };

  handleCancelOrder = () => {
    const order = this.state.selectedOrder.copy()
    order.status = Order.OrderStatus.CANCELED

    OrderAction.updateOrder(order)
    this.setState({
      isLoadingCancelOrder: true,
    })
  }

  //
  // Order detail modal
  //

  onHideModal = () => {
    this.setState({
      selectedOrder: null,
      errorCancel: null,
      openedModalType: null,
    });
  }

  //
  // Renderers
  //

  renderHeader() {
    return (
      <div className="row col-xs-12">
        <div
          className="col-xs-12 col-md-4 col-md-offset-4 top-buffer-sm-20 top-buffer-xs-20"
        >
          <DateField
            onChange={this.onDateChange}
            date={this.state.date}
            maxDate={moment().endOf('day')}
          />
        </div>
      </div>
    );
  }

  renderOrderDetailModal() {
    if (!_.isNull(this.state.selectedOrder) && this.state.openedModalType === ModalType.DETAIL) {
      return (
        <OrderDetailModal
          order={this.state.selectedOrder}
          show
          onHide={this.onHideModal}
          currencyCode={this.state.restaurant.currency}
        />
      );
    }
  }

  renderOrderCancelModal() {
    if (!_.isNull(this.state.selectedOrder) && this.state.openedModalType === ModalType.CANCEL) {
      return (
        <OrderCancelModal
          order={this.state.selectedOrder}
          show
          isLoading={this.state.isLoadingCancelOrder}
          onHide={this.onHideModal}
          onSubmit={this.handleCancelOrder}
          errorCancel={this.state.errorCancel}
          currencyCode={this.state.restaurant.currency}
        />
      );
    }
  }

  renderNoOrders() {
    return (
      <div className="text-center">
        <h3>{I18n.tr('order.history.no_orders')}</h3>
      </div>
    );
  }

  renderOrders() {
    const historyOrders = this.state.ordersList.getHistoryOrders();

    if (_.isEmpty(historyOrders)) {
      return this.renderNoOrders();
    }

    const sortedHistoryOrders = _.sortBy(historyOrders, ['desc'],
    (order: Order): number => {
      return order.dueDate.valueOf();
    });

    const rows = sortedHistoryOrders.map((order: Order) => {
      if (!order.isFailed()) {
        const selected = !_.isNull(this.state.selectedOrder)
        && this.state.selectedOrder.id === order.id;

        return (
          <OrderRowView
            order={order}
            key={order.id}
            onOrdersListChange={this.onOrderChange}
            onSelected={this.onOrderSelected}
            selected={selected}
            currencyCode={this.state.restaurant.currency}
          />
        );
      }
      return (null);
    });

    return (
      <div className="table_container table-responsive">
        <table className="orders_ready table">
          <thead>
            <tr>
              <th className="text-left">
                <i className="fa fa-clock-o" />
              </th>
              <th />
              <th>
                {I18n.tr('order.history.table.header.first_name')}
              </th>
              <th
                className="hidden-xs hidden-sm"
              >
                {I18n.tr('order.history.table.header.phone')}
              </th>
              <th>
                {I18n.tr('order.history.table.header.total')}
              </th>
              <th />
              <th className="text-center">
                {I18n.tr('order.history.table.header.status')}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows}
          </tbody>
        </table>
      </div>
    );
  }

  render() {
    const ordersList:OrdersList = this.state.ordersList;

    let contentView = (null);

    try {
    if (_.isNull(ordersList)) {
      contentView = this.renderLoading();
    } else {
      contentView = (
        <div className="order_list__content row">
          {this.renderHeader()}

          <div className="col-xs-12 order_list__list top-buffer-50">
            {this.renderOrders()}
          </div>

          {this.renderOrderDetailModal()}
          {this.renderOrderCancelModal()}
        </div>
      );
    }

    return (
      <div className="order_list">
        {this.renderLoading()}
        {contentView}
      </div>
    );
  } catch (e) { console.error(e); }
  }

}
